import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Plus, FolderOpen, Trash2, Pencil, Check } from 'lucide-react';
import FileDrop from '../components/FileDrop';
import ContractTypeSelect from '../components/ContractTypeSelect';
import Button from '../components/ui/Button';
import { listMatters, createMatter, deleteMatter, patchMatter } from '../api/matters';
import { getContractTypes } from '../api/contractTypes';
import type { MatterSummary, ContractType } from '../types';

const RISK_COLOR: Record<string, string> = {
  'высокий': 'bg-red-100 text-red-800',
  'средний': 'bg-amber-100 text-amber-800',
  'низкий': 'bg-green-100 text-green-800',
};

export default function MattersPage() {
  const navigate = useNavigate();
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState('');
  const [counterparty, setCounterparty] = useState('');
  // Default to the supplier/executor side (side_a) so the button is preselected.
  const [role, setRole] = useState('side_a');
  const [contractType, setContractType] = useState('');
  const [types, setTypes] = useState<ContractType[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const startRename = (m: MatterSummary, ev: React.MouseEvent) => {
    ev.stopPropagation();
    setEditingId(m.id);
    setEditTitle(m.title || '');
  };
  const saveRename = async (id: number) => {
    const title = editTitle.trim();
    setEditingId(null);
    const prev = matters.find((x) => x.id === id);
    if (!prev || title === (prev.title || '')) return;
    setMatters((list) => list.map((x) => x.id === id ? { ...x, title } : x));  // optimistic
    try { await patchMatter(id, { title }); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Не удалось переименовать'); refresh(); }
  };

  const canCreate = (!!file || text.trim().length > 0) && !!role;

  const refresh = async () => {
    setLoadingList(true);
    try {
      setMatters(await listMatters());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Не удалось загрузить дела');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => { getContractTypes().then(setTypes).catch((e) => console.warn('contract-types load failed', e)); }, []);

  useEffect(() => {
    if (!creating) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [creating]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleCreate = async () => {
    if (!canCreate) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCreating(true);
    setError(null);
    try {
      const m = await createMatter({ title, counterparty, role, contract_type: contractType, file, text }, controller.signal);
      navigate(`/matters/${m.id}`);
    } catch (e: any) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setError(e?.response?.data?.detail || e?.message || 'Не удалось создать договор');
    } finally {
      if (mountedRef.current) setCreating(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const handleDelete = async (id: number, ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (!window.confirm('Удалить договор?')) return;
    try {
      await deleteMatter(id);
      setMatters((prev) => prev.filter((m) => m.id !== id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-rose-500" />
          <h2 className="text-lg font-semibold text-gray-800">Договоры</h2>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="w-4 h-4" /> Новый договор
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {showForm && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-w-2xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Название (необяз., заполнит ИИ)"
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-pink-500"
            />
            <input
              value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
              placeholder="Контрагент"
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-pink-500"
            />
          </div>
          <div className="mb-3">
            <ContractTypeSelect
              types={types}
              contractType={contractType}
              role={role}
              onChangeType={setContractType}
              onChangeRole={setRole}
              disabled={creating}
              center={false}
            />
          </div>
          <FileDrop file={file} onFileChange={setFile} onError={setError} disabled={creating || text.trim().length > 0} />
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Или вставьте текст:</label>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)} disabled={creating || !!file}
              placeholder="Вставьте текст договора..."
              className="w-full h-28 px-3 py-2 border border-gray-300 rounded-lg resize-none outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100"
            />
          </div>
          <div className="mt-3 text-center">
            <Button onClick={handleCreate} disabled={creating || !canCreate} loading={creating}>
              {creating ? `Анализирую... ${elapsed}с` : 'Загрузить и проанализировать'}
            </Button>
            {!creating && (!!file || text.trim().length > 0) && !role && (
              <p className="mt-2 text-sm text-amber-600">Выберите, с чьей стороны анализируем</p>
            )}
          </div>
          {creating && (
            <div className="mt-4 flex justify-center">
              <img src="/masha_reading_contracts.gif" alt="Маша изучает договор..." style={{ maxWidth: '260px', width: '100%', borderRadius: '16px' }} />
            </div>
          )}
        </div>
      )}

      {loadingList ? (
        <div className="text-center text-gray-400 py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
      ) : matters.length === 0 ? (
        <div className="text-center text-gray-500 py-10">Пока нет договоров. Загрузите первый.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {matters.map((m) => (
            <div key={m.id} onClick={() => navigate(`/matters/${m.id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 cursor-pointer hover:border-pink-300 hover:shadow transition">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {editingId === m.id ? (
                    <input
                      autoFocus
                      value={editTitle}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onBlur={() => saveRename(m.id)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') { e.preventDefault(); saveRename(m.id); }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="w-full font-semibold text-gray-800 border border-pink-300 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-pink-400/50"
                    />
                  ) : (
                    <h3 className="font-semibold text-gray-800 truncate">{m.title || 'Без названия'}</h3>
                  )}
                  {m.subject && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{m.subject}</p>}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {editingId === m.id ? (
                    <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveRename(m.id); }}
                      className="p-1 text-gray-300 hover:text-green-600" title="Сохранить">
                      <Check className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={(e) => startRename(m, e)} className="p-1 text-gray-300 hover:text-pink-500" title="Переименовать">
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={(e) => handleDelete(m.id, e)} className="p-1 text-gray-300 hover:text-red-500" title="Удалить">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-2 space-y-0.5">
                <p className="text-xs text-gray-600 truncate">
                  {m.our_party
                    ? <><span className="text-gray-400">От:</span> {m.our_party} <span className="text-gray-400">→</span> {m.counterparty || '—'}</>
                    : <><span className="text-gray-400">Контрагент:</span> {m.counterparty || '—'}</>}
                </p>
                {(m.contract_number || m.contract_date) && (
                  <p className="text-xs text-gray-400">
                    {m.contract_number ? `№ ${m.contract_number}` : ''}
                    {m.contract_number && m.contract_date ? ' · ' : ''}
                    {m.contract_date || ''}
                  </p>
                )}
              </div>

              <div className="flex items-center flex-wrap gap-2 mt-3">
                {m.contract_type && (
                  <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700">
                    {types.find((t) => t.id === m.contract_type)?.label || m.contract_type}
                  </span>
                )}
                {m.overall_risk && (
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${RISK_COLOR[m.overall_risk] || 'bg-gray-100 text-gray-700'}`}>
                    риск: {m.overall_risk}
                  </span>
                )}
                <span className="text-xs text-gray-500">позиция: {m.role} · пунктов {m.clauses_count} · рисков {m.risks_count}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
